// Build (SAA-2026-008 UI3.1–UI3.2): chọn mẫu · nhập JSON · cấu trúc (Ngừng dùng / Dùng lại, thêm trường) · lịch sử + « Quay về
// phiên bản này ». Mọi thay đổi đi qua ChangeReview (diff → duyệt → áp dụng). Chỉ manager thấy (layout ẩn tab).
import type { BlueprintSpec } from "@secretaa/contracts-data";
import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, EmptyState, Field, VersionTimeline } from "@secretaa/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import type { z } from "zod";
import { api } from "../../app/api.ts";
import { useCurrentWorkspace } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { FormError, Notice, PageHeader } from "../../shared/form.tsx";
import { labelOf } from "../data/manifest-view.ts";
import { useBlueprint, useProject } from "../project/project-hooks.ts";
import { RoleNeeded } from "../project/role-hint.tsx";
import { ChangeReview } from "./change-review.tsx";

type Spec = z.input<typeof BlueprintSpec>;

function useReview() {
  const [proposal, setProposal] = useState<Spec | null>(null);
  const [applied, setApplied] = useState<number | null>(null);
  return { proposal, applied, propose: (s: Spec) => (setApplied(null), setProposal(s)), done: (v: number) => (setProposal(null), setApplied(v)), cancel: () => setProposal(null) };
}

export function BuildPage() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const { projectId = "" } = useParams();
  const project = useProject(projectId);
  const bp = useBlueprint(projectId);
  const templates = useQuery({ queryKey: qk.blueprintTemplates(cur.tenant), queryFn: () => api.blueprint.templates(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const review = useReview();
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  if (bp.isPending || project.isPending) return <p>{t("app.loading")}</p>;
  if (bp.isError) return <FormError message={errorMessage(t, bp.error)} />;
  if (project.isError) return <FormError message={errorMessage(t, project.error)} />;
  const p = project.data.data;
  return (
    <>
      <PageHeader title={t("build.blueprint")} />
      <RoleNeeded projectId={projectId} needs="manager" />
      <p className="mb-4 text-sm text-(--saa-fg-muted)">{bp.data.activeVersion ? t("build.currentVersion", { version: bp.data.activeVersion }) : t("build.noVersion")}</p>
      {review.applied ? (
        <Notice>
          {t("build.applied", { version: review.applied })}{" "}
          <Link className="underline" to={`/w/${cur.workspaceId}/projects/${projectId}/data`}>
            {t("build.openData")}
          </Link>
        </Notice>
      ) : null}
      {review.proposal ? (
        <ChangeReview projectId={projectId} projectKey={p.key} proposal={review.proposal} onDone={review.done} onCancel={review.cancel} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <section aria-labelledby="tpl" className="flex flex-col gap-3">
            <h2 id="tpl" className="text-base font-semibold">
              {t("build.template")}
            </h2>
            {templates.isError ? <FormError message={errorMessage(t, templates.error)} /> : null}
            {(templates.data ?? []).map((x) => (
              <Card key={x.key} className="flex flex-col gap-2 p-4">
                <h3 className="font-medium">{labelOf(x.label, i18n.language)}</h3>
                <p className="text-sm text-(--saa-fg-muted)">{labelOf(x.description, i18n.language)}</p>
                <div>
                  <Button onClick={() => review.propose(x.spec)}>{t("build.useTemplate")}</Button>
                </div>
              </Card>
            ))}
          </section>
          <section aria-labelledby="imp" className="flex flex-col gap-3">
            <h2 id="imp" className="text-base font-semibold">
              {t("build.import.title")}
            </h2>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              {t("build.import.label")}
              <textarea className="min-h-48 rounded-md border border-(--saa-border) bg-(--saa-surface) p-2 font-mono text-xs" value={json} onChange={(e) => setJson(e.target.value)} />
            </label>
            {jsonError ? <FormError message={jsonError} /> : null}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={!json.trim()}
                onClick={() => {
                  try {
                    setJsonError(null);
                    review.propose(JSON.parse(json) as Spec);
                  } catch {
                    setJsonError(t("build.import.invalid"));
                  }
                }}
              >
                {t("build.import.preview")}
              </Button>
              {bp.data.activeVersion ? (
                <Button variant="ghost" onClick={() => setJson(JSON.stringify(bp.data.spec, null, 2))}>
                  {t("build.import.loadCurrent")}
                </Button>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

const FIELD_TYPES = ["text", "number", "money", "date", "bool"] as const;

export function StructurePage() {
  const { t, i18n } = useTranslation();
  const { projectId = "" } = useParams();
  const project = useProject(projectId);
  const bp = useBlueprint(projectId);
  const review = useReview();
  const [adding, setAdding] = useState<{ collectionId: string; label: string; key: string; type: (typeof FIELD_TYPES)[number] } | null>(null);
  if (bp.isPending || project.isPending) return <p>{t("app.loading")}</p>;
  if (bp.isError) return <FormError message={errorMessage(t, bp.error)} />;
  if (project.isError) return <FormError message={errorMessage(t, project.error)} />;
  const spec = bp.data.spec;
  if (!bp.data.activeVersion) return <EmptyState title={t("build.noVersion")} />;
  const toggle = (fieldId: string) =>
    review.propose({ ...spec, collections: spec.collections.map((c) => ({ ...c, fields: c.fields.map((f) => (f.id === fieldId ? { ...f, status: f.status === "ACTIVE" ? ("RETIRED" as const) : ("ACTIVE" as const) } : f)) })) });
  const addField = () => {
    if (!adding) return;
    const f = { id: `tmp_${adding.key}`, key: adding.key, label: { vi: adding.label, en: adding.label }, required: false, indexed: true, status: "ACTIVE" as const, ...(adding.type === "money" ? { type: "money" as const, currency: "VND" } : { type: adding.type }) };
    review.propose({ ...spec, collections: spec.collections.map((c) => (c.id === adding.collectionId ? { ...c, fields: [...c.fields, f] } : c)) } as Spec);
    setAdding(null);
  };
  return (
    <>
      <PageHeader title={t("build.structure")} actions={!review.proposal ? <Button onClick={() => setAdding({ collectionId: spec.collections[0]?.id ?? "", label: "", key: "", type: "text" })}>{t("build.field.add")}</Button> : undefined} />
      <RoleNeeded projectId={projectId} needs="manager" />
      {review.applied ? <Notice>{t("build.applied", { version: review.applied })}</Notice> : null}
      {review.proposal ? <ChangeReview projectId={projectId} projectKey={project.data.data.key} proposal={review.proposal} onDone={review.done} onCancel={review.cancel} /> : null}
      {adding && !review.proposal ? (
        <Card className="mb-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("data.collection")}
            <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={adding.collectionId} onChange={(e) => setAdding({ ...adding, collectionId: e.target.value })}>
              {spec.collections
                .filter((c) => c.status === "ACTIVE")
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {labelOf(c.label, i18n.language)}
                  </option>
                ))}
            </select>
          </label>
          <Field label={t("build.field.label")} value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} />
          <Field label={t("build.field.key")} hint={t("build.field.keyHint")} value={adding.key} onChange={(e) => setAdding({ ...adding, key: e.target.value.toLowerCase() })} />
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("build.field.type")}
            <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={adding.type} onChange={(e) => setAdding({ ...adding, type: e.target.value as (typeof FIELD_TYPES)[number] })}>
              {FIELD_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`data.type.${x}`)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-2">
            <Button disabled={!adding.label.trim() || !/^[a-z][a-z0-9_]{0,63}$/.test(adding.key)} onClick={addField}>
              {t("build.diff.preview")}
            </Button>
            <Button variant="secondary" onClick={() => setAdding(null)}>
              {t("common.cancel")}
            </Button>
          </div>
        </Card>
      ) : null}
      <div className="flex flex-col gap-4">
        {spec.collections.map((c) => (
          <Card key={c.id} className="p-4">
            <h2 className="mb-2 font-medium">
              {labelOf(c.label, i18n.language)} {c.status === "RETIRED" ? <span className="text-xs text-(--saa-fg-muted)">· {t("data.field.retired")}</span> : null}
            </h2>
            <ul className="divide-y divide-(--saa-border) text-sm">
              {c.fields.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    {labelOf(f.label, i18n.language)} <span className="text-xs text-(--saa-fg-muted)">· {t(`data.type.${f.type}`)}</span>
                    {f.status === "RETIRED" ? <span className="ml-2 rounded bg-(--saa-muted) px-1.5 py-0.5 text-xs">{t("data.field.retired")}</span> : null}
                  </span>
                  <Button variant="secondary" disabled={!!review.proposal} onClick={() => toggle(f.id)}>
                    {f.status === "ACTIVE" ? t("data.field.retire") : t("data.field.reactivate")}
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </>
  );
}

export function HistoryPage() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const { projectId = "" } = useParams();
  const project = useProject(projectId);
  const bp = useBlueprint(projectId);
  const review = useReview();
  const versions = useQuery({ queryKey: qk.blueprintVersions(cur.tenant, projectId), queryFn: () => api.blueprint.versions(cur.workspaceId, projectId), staleTime: STALE_TIME.projectDefinition });
  const [loading, setLoading] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  if (versions.isPending || project.isPending || bp.isPending) return <p>{t("app.loading")}</p>;
  if (versions.isError) return <FormError message={errorMessage(t, versions.error)} />;
  if (project.isError) return <FormError message={errorMessage(t, project.error)} />;
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const restore = async (v: number) => {
    setLoading(v);
    setError(null);
    try {
      review.propose((await api.blueprint.version(cur.workspaceId, projectId, v)).spec);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(null);
    }
  };
  return (
    <>
      <PageHeader title={t("build.history")} />
      <RoleNeeded projectId={projectId} needs="manager" />
      {review.applied ? <Notice>{t("build.applied", { version: review.applied })}</Notice> : null}
      {error ? <FormError message={errorMessage(t, error)} /> : null}
      {review.proposal ? <ChangeReview projectId={projectId} projectKey={project.data.data.key} proposal={review.proposal} onDone={review.done} onCancel={review.cancel} /> : null}
      {versions.data.length === 0 ? (
        <EmptyState title={t("build.noVersion")} />
      ) : (
        <Card>
          <VersionTimeline
            title={t("build.history")}
            riskLabel={(r) => t(`build.risk.${r.toLowerCase()}`)}
            items={versions.data.map((v) => ({
              version: v.version,
              title: t("build.versionTitle", { version: v.version, count: v.ops }),
              risk: v.risk,
              createdAt: v.createdAt,
              createdAtText: fmt.format(new Date(v.createdAt)),
              current: v.version === bp.data?.activeVersion,
              currentLabel: t("build.current"),
              actions:
                v.version !== bp.data?.activeVersion ? (
                  <Button variant="secondary" disabled={loading !== null || !!review.proposal} onClick={() => void restore(v.version)}>
                    {t("build.rollback")}
                  </Button>
                ) : undefined,
            }))}
          />
        </Card>
      )}
    </>
  );
}
